import { useRef } from "react";
import axios from "axios";
import useAxiosConfig from "../../../config/useAxiosConfig";

const ENDPOINT = "/InstructorWelcomeEmail/";

// ColdFusion can serialize struct keys in upper case (SUCCESS, MESSAGE)
// depending on how the struct was built, so read the response without
// depending on key casing.
const lowerCaseKeys = (data) =>
  data && typeof data === "object" && !Array.isArray(data)
    ? Object.fromEntries(
        Object.entries(data).map(([key, value]) => [key.toLowerCase(), value]),
      )
    : {};

// Sends the instructor welcome email through the InstructorWelcomeEmail
// ColdFusion API. useAxiosConfig points axios at the API base URL and adds
// the API key, so the endpoint is a relative path like the other CF calls.
const useInstructorWelcomeEmail = () => {
  const { loading: axiosLoading } = useAxiosConfig();

  // The polling loop below outlives the render that started it, so it has to
  // read the latest value from a ref rather than the value it closed over.
  const axiosLoadingRef = useRef(axiosLoading);
  axiosLoadingRef.current = axiosLoading;

  const waitForAxiosReady = (timeoutMs = 10000) =>
    new Promise((resolve, reject) => {
      const intervalMs = 100;
      let waited = 0;
      const timer = setInterval(() => {
        if (!axiosLoadingRef.current) {
          clearInterval(timer);
          resolve();
          return;
        }
        waited += intervalMs;
        if (waited >= timeoutMs) {
          clearInterval(timer);
          reject(new Error("The email service is not ready. Please try again."));
        }
      }, intervalMs);
    });

  // payload: { toEmail, instructorName, subject, body }
  // Resolves with { message, sentTo } when the email was sent; throws an
  // Error with a readable message otherwise.
  const send = async ({ toEmail, instructorName, subject, body }) => {
    if (axiosLoadingRef.current) {
      await waitForAxiosReady();
    }

    // ColdFusion reads the payload from a single form field holding JSON.
    const params = new URLSearchParams();
    params.append(
      "jsStruct",
      JSON.stringify({ toEmail, instructorName, subject, body }),
    );

    let response;
    try {
      response = await axios.post(ENDPOINT, params.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
    } catch (error) {
      const data = lowerCaseKeys(error.response?.data);
      const status = error.response?.status;
      throw new Error(
        data.message ||
          (status
            ? `The welcome email could not be sent (HTTP ${status}).`
            : "The welcome email could not be sent."),
      );
    }

    // A 2xx response means the API accepted and sent it (the other CF email
    // APIs work the same way); only an explicit success:false is a failure.
    const data = lowerCaseKeys(response.data);
    if (data.success === false) {
      throw new Error(data.message || "The welcome email could not be sent.");
    }
    return { message: data.message, sentTo: data.sentto };
  };

  return { send };
};

export default useInstructorWelcomeEmail;
